import { fetchBestPriceForOffer } from 'actions/OfferActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { isLEHotel } from 'lib/offer/offerTypes'
import { buildSearchParamsKey, buildSuggestedDatesParamsKey } from 'lib/search/searchUtils'
import { useEffect, useMemo } from 'react'
import { getSuggestedDates } from 'selectors/offerSelectors'
import { max } from 'lib/array/arrayUtils'

export function useOfferBestPrice(offer: App.AnyOffer, filters: App.OfferListFilters = {}) {
  const bestPrices = useAppSelector((state) => state.offer.offerBestPrices[offer.id])
  const priceErrors = useAppSelector((state) => state.offer.offerPricesErrors[offer.id])
  const dispatch = useAppDispatch()
  const searchKey = useMemo(() => buildSearchParamsKey(filters.checkIn, filters.checkOut, filters.rooms), [filters])
  const isHotelType = isLEHotel(offer)

  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(filters.flexibleMonths, filters.flexibleNights, filters.rooms)
  const suggestedDates = useAppSelector((state) => getSuggestedDates(state, flexibleSearchFilterKey, offer.id))

  const bestPrice = bestPrices ? bestPrices[searchKey] : undefined
  const bestPriceError = priceErrors ? priceErrors[searchKey] : undefined
  const available = bestPrice?.available
  const rate = available ? bestPrice.rate : undefined

  useEffect(() => {
    if (!suggestedDates && !!filters.checkIn && isHotelType && !!filters.checkOut && !!filters.rooms && filters.rooms.length > 0) {
      dispatch(fetchBestPriceForOffer(offer, {
        checkIn: filters.checkIn,
        checkOut: filters.checkOut,
        occupants: filters.rooms,
      }))
    }
  }, [dispatch, filters, isHotelType, offer, suggestedDates])

  const bestPricePackage = useMemo(() => {
    if (!isHotelType) {
      return undefined
    }
    if (suggestedDates) {
      const applicablePackages = offer.packages?.filter(pkg => pkg.availableRooms && pkg.id === suggestedDates.packageId && pkg.price === suggestedDates.price && suggestedDates.value === pkg.value)
      return max(applicablePackages, (pkg) => pkg.availableRooms ?? 0)
    }
    if (!filters.checkIn || bestPriceError) {
      return offer.lowestPricePackage
    }
    if (available) {
      return offer.packages.find(pkg => pkg.uniqueKey === rate?.packageUniqueKey)
    }
  }, [isHotelType, suggestedDates, filters.checkIn, bestPriceError, available, offer, rate?.packageUniqueKey])
  return bestPricePackage
}
